/**
 * RevealAssignmentsOverlay.tsx — "Reveal my secret persons" (task I07).
 *
 * Shown only when the backend grants `canRevealAssignments` (Started and
 * Ended — computed server-side from role + status, D02; the page renders
 * the entry button on that flag only, never on local state).
 *
 * Secrecy (locked rule + F05/I07 acceptance): the overlay fetches
 * `GET /games/:id/my-assignments` ONLY when it is opened. The GamePage
 * never preloads assignment data, so recipient names are absent from the
 * initial HTML and from every API payload until the user explicitly
 * opens this overlay. Assignments are immutable once the game has
 * started (locked rule), so there is no realtime refetch while open —
 * each open performs exactly one fetch, and closing discards the data
 * (it is kept in no page-level state).
 * The server is the final authority (F04): it returns only the
 * authenticated giver's recipients; there is no client parameter to
 * name another giver. Dialog behavior (focus trap, Escape, focus return,
 * ARIA) comes from the shared K01 Modal.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiClientError, type AssignmentRecipient } from '@shared';
import { apiV1 } from '../api';
import { Modal } from './Modal';

interface RevealAssignmentsOverlayProps {
  gameId: number;
  onClose: () => void;
}

export function RevealAssignmentsOverlay({ gameId, onClose }: RevealAssignmentsOverlayProps) {
  const [recipients, setRecipients] = useState<AssignmentRecipient[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Fetched ONLY when the overlay is opened (component mount).
  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setLoadError(null);
    try {
      setRecipients(await apiV1.myAssignments(gameId));
    } catch (err) {
      setLoadError(err instanceof ApiClientError ? err.message : 'Could not load your secret persons.');
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Modal title="My secret persons" onClose={onClose} blocked={loading}>
        {loading && recipients === null && (
          <p className="overlay-status" aria-live="polite">
            Loading…
          </p>
        )}

        {loadError !== null && recipients === null && (
          <p className="auth-error" role="alert">
            {loadError}
          </p>
        )}

        {recipients !== null && (
          <>
            <p className="reveal-intro">
              {recipients.length === 1
                ? 'You are giving a gift to:'
                : 'You are giving gifts to:'}
            </p>
            <ul className="reveal-list">
              {recipients.map((person) => (
                <li key={person.userId} className="reveal-person">
                  {person.username}
                </li>
              ))}
            </ul>
            <p className="reveal-note">Keep it a secret until the big day. 🎅</p>
            <div className="overlay-actions">
              <button type="button" className="game-refresh" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        )}
    </Modal>
  );
}
